import type { CameraView } from "./coords.ts";
import { chunksTouched, type ChunkRef } from "./map-overview.ts";
import type { CellRect } from "../terrain/match.ts";

/**
 * The map overview's view arithmetic (ADR-109's amendment of 2026-10-13, decisions 35, 40, 41 and 46): the zoom
 * limits of the overview's own camera, a step of its wheel, the camera a centre and a zoom make, where it may
 * look, and what is a click and what is a pan. Plain data and whole arithmetic; no camera, no canvas.
 */

/**
 * The room kept round the fitted map on each side, in screen pixels, so the map's corner and its label are not
 * on the window's edge.
 */
export const OVERVIEW_MARGIN = 24;

/**
 * How many maps across the nearest zoom out shows: the nine-map fit is three maps by three.
 */
export const OVERVIEW_MIN_SPAN = 3;

/**
 * The nearest zoom in, in screen pixels to a picture pixel (decision 49).
 */
export const OVERVIEW_MAX_SCREEN_PER_PICTURE_PIXEL = 2;

/**
 * The factor one wheel notch or zoom key changes the overview's zoom by.
 */
export const OVERVIEW_ZOOM_FACTOR = 1.25;

/**
 * The most a pointer may move between press and release for the release to be a click, in screen pixels
 * (decision 41: a drag of more than 4 pixels is a pan).
 */
export const OVERVIEW_CLICK_SLOP = 4;

/**
 * The zooms the overview's camera may take: the fit of the map alone, the nine-map fit and the ceiling.
 */
export interface OverviewLimits {
  readonly fit: number;
  readonly min: number;
  readonly max: number;
}

/**
 * A rectangle in world pixels.
 */
export interface WorldBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Throws unless a value is a positive finite number.
 *
 * @param value The value.
 * @param name What it is, for the message.
 * @throws RangeError when it is not.
 */
function requirePositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive number, got ${value}`);
  }
}

/**
 * The room a window leaves for the map once the margin is taken, never less than half the window so a small
 * window still gives a usable zoom.
 *
 * @param size The window's side in pixels.
 * @returns The room in pixels.
 */
function roomOf(size: number): number {
  return Math.max(size - 2 * OVERVIEW_MARGIN, size / 2);
}

/**
 * The zoom limits of the overview's camera for a map. The zoom is screen pixels to a world pixel, and the world
 * is the tile size times the map's tiles, so the picture, `scale` pixels a tile, is drawn at `tileSize / scale`
 * world pixels to a picture pixel. The fit shows the whole map with the margin; the nearest zoom out shows three
 * maps across; the ceiling is 2 screen pixels to a picture pixel. The fit is never past the ceiling, so a large
 * window does not blow a map up beyond it.
 *
 * @param map The map's size in tiles.
 * @param tileSize The world's tile size in pixels.
 * @param scale Pixels a tile of the picture.
 * @param window The window's size in pixels.
 * @returns The three zooms.
 * @throws RangeError when a size is not a positive number.
 */
export function overviewLimits(map: { readonly width: number; readonly height: number }, tileSize: number, scale: number, window: { readonly width: number; readonly height: number }): OverviewLimits {
  requirePositive(map.width, "map width");
  requirePositive(map.height, "map height");
  requirePositive(tileSize, "tile size");
  requirePositive(scale, "scale");
  requirePositive(window.width, "window width");
  requirePositive(window.height, "window height");
  const max = (OVERVIEW_MAX_SCREEN_PER_PICTURE_PIXEL * scale) / tileSize;
  const fit = Math.min(roomOf(window.width) / (map.width * tileSize), roomOf(window.height) / (map.height * tileSize), max);
  return { fit, min: fit / OVERVIEW_MIN_SPAN, max };
}

/**
 * One step of the overview's zoom, kept inside its limits.
 *
 * @param current The zoom now.
 * @param direction Positive zooms in, negative out, zero only clamps.
 * @param limits The limits.
 * @returns The zoom after the step.
 */
export function overviewStep(current: number, direction: number, limits: OverviewLimits): number {
  const stepped = direction > 0 ? current * OVERVIEW_ZOOM_FACTOR : direction < 0 ? current / OVERVIEW_ZOOM_FACTOR : current;
  return Math.min(limits.max, Math.max(limits.min, stepped));
}

/**
 * The camera view a centre and a zoom make, in the shape the shared coordinate helpers read.
 *
 * @param centre The world point at the window's centre.
 * @param zoom Screen pixels to a world pixel.
 * @param window The window's size in pixels.
 * @returns The view.
 */
export function overviewViewOf(centre: { readonly x: number; readonly y: number }, zoom: number, window: { readonly width: number; readonly height: number }): CameraView {
  return { scrollX: centre.x - window.width / 2, scrollY: centre.y - window.height / 2, width: window.width, height: window.height, zoom };
}

/**
 * The world point at a view's centre.
 *
 * @param view The view.
 * @returns The point.
 */
export function overviewCentreOf(view: CameraView): { x: number; y: number } {
  return { x: view.scrollX + view.width / 2, y: view.scrollY + view.height / 2 };
}

/**
 * Keeps the view's centre within one map's width or height of the map's edge on every side, so a pan can bring the
 * ring of maps round it into view and never leaves the map out of reach.
 *
 * @param centre The wanted centre.
 * @param map The map's rectangle in world pixels.
 * @returns The centre, pulled back when it was further out.
 */
export function overviewCentreClamped(centre: { readonly x: number; readonly y: number }, map: WorldBox): { x: number; y: number } {
  return {
    x: Math.min(map.x + map.width * 2, Math.max(map.x - map.width, centre.x)),
    y: Math.min(map.y + map.height * 2, Math.max(map.y - map.height, centre.y)),
  };
}

/**
 * Whether a pointer that went down at one place and is now at another has dragged: a move of more than 4 pixels
 * in either direction is a pan and never a click.
 *
 * @param from Where it went down.
 * @param to Where it is.
 * @returns True for a drag.
 */
export function isPanDrag(from: { readonly x: number; readonly y: number }, to: { readonly x: number; readonly y: number }): boolean {
  return Math.abs(to.x - from.x) > OVERVIEW_CLICK_SLOP || Math.abs(to.y - from.y) > OVERVIEW_CLICK_SLOP;
}

/**
 * The world point at the middle of a tile.
 *
 * @param tile The tile.
 * @param tileSize The world's tile size in pixels.
 * @returns The point in world pixels.
 */
export function tileCentre(tile: { readonly x: number; readonly y: number }, tileSize: number): { x: number; y: number } {
  return { x: (tile.x + 0.5) * tileSize, y: (tile.y + 0.5) * tileSize };
}

/**
 * The chunks a change to a layer's props touches, for the overview's picture to draw again: the chunks the boxes of
 * every prop that was added, removed or replaced reach, on either side of the change. A prop is the same when it is
 * the same object; the store replaces a prop it edits. Each chunk is named once, in the order first met, the old
 * side first.
 *
 * @param before The layer's props before.
 * @param after The layer's props after.
 * @param boxOf A prop's box in tiles (`x0` and `y0` inclusive, `x1` and `y1` exclusive), or null when its frame has none.
 * @param chunkSize The chunk's side in tiles.
 * @param chunksWide The map's width in chunks.
 * @param chunksHigh The map's height in chunks.
 * @returns The chunks.
 * @throws RangeError when the chunk size or a count is not a positive whole number.
 */
export function changedPropChunks<P extends { readonly id: string }>(before: readonly P[], after: readonly P[], boxOf: (prop: P) => CellRect | null, chunkSize: number, chunksWide: number, chunksHigh: number): ChunkRef[] {
  const afterById = new Map(after.map((p) => [p.id, p]));
  const beforeById = new Map(before.map((p) => [p.id, p]));
  const changed: P[] = [];
  for (const prop of before) {
    if (afterById.get(prop.id) !== prop) {
      changed.push(prop);
    }
  }
  for (const prop of after) {
    if (beforeById.get(prop.id) !== prop) {
      changed.push(prop);
    }
  }
  const seen = new Set<number>();
  const chunks: ChunkRef[] = [];
  for (const prop of changed) {
    const box = boxOf(prop);
    if (box === null) {
      continue;
    }
    for (const chunk of chunksTouched(box, chunkSize, chunksWide, chunksHigh)) {
      const key = chunk.cy * chunksWide + chunk.cx;
      if (!seen.has(key)) {
        seen.add(key);
        chunks.push(chunk);
      }
    }
  }
  return chunks;
}
